import { useCallback, useEffect, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"

import type { ContactsSelection } from "@productlane/zero/queries"
import { queries } from "@productlane/zero/queries"

import { isQuerySettled } from "@/lib/query-settled"
import { useContactsFilterState } from "../filter/contacts-filter-state"
import { useContactsSortState } from "./contacts-sort-state"

export const CONTACTS_PAGE_SIZE = 200

type SortColumn = "name" | "createdAt" | "status"

/** The server order for a sort choice; a saved sort the list no longer offers falls back to name. */
export function resolveContactsSort(sortType: string): {
	sortColumn: SortColumn
	sortOrder: "asc" | "desc"
} {
	switch (sortType) {
		case "name-desc":
			return { sortColumn: "name", sortOrder: "desc" }
		case "status-asc":
			return { sortColumn: "status", sortOrder: "asc" }
		case "status-desc":
			return { sortColumn: "status", sortOrder: "desc" }
		case "newest":
			return { sortColumn: "createdAt", sortOrder: "desc" }
		case "oldest":
			return { sortColumn: "createdAt", sortOrder: "asc" }
		default:
			return { sortColumn: "name", sortOrder: "asc" }
	}
}

/** The filters the list applies, shared with select-all so both match the same contacts. */
export function useContactsFilters(): ContactsSelection["filters"] {
	const { companyIds, subscribedStates, sources, negations } =
		useContactsFilterState()
	return useMemo(() => {
		if (
			companyIds.length === 0 &&
			subscribedStates.length === 0 &&
			sources.length === 0
		)
			return undefined
		return {
			negations,
			companyIds: companyIds.length > 0 ? companyIds : undefined,
			subscribedStates:
				subscribedStates.length > 0 ? subscribedStates : undefined,
			sources: sources.length > 0 ? sources : undefined,
		}
	}, [companyIds, subscribedStates, sources, negations])
}

/** The People list, searched, filtered and sorted on the server, a page at a time. */
export function usePagedContacts(search: string) {
	const filters = useContactsFilters()
	const { sortType } = useContactsSortState()
	const { sortColumn, sortOrder } = resolveContactsSort(sortType)
	const trimmed = search.trim()

	const [limit, setLimit] = useState(CONTACTS_PAGE_SIZE)
	const resetKey = JSON.stringify([filters, trimmed, sortColumn, sortOrder])
	useEffect(() => setLimit(CONTACTS_PAGE_SIZE), [resetKey])

	const [contacts, result] = useQuery(
		queries.contactsPage({
			filters,
			search: trimmed || undefined,
			sortColumn,
			sortOrder,
			limit,
		}),
	)

	const hasMore = contacts.length >= limit
	const loadMore = useCallback(() => {
		if (hasMore) setLimit((current) => current + CONTACTS_PAGE_SIZE)
	}, [hasMore])

	return {
		contacts,
		filters,
		search: trimmed,
		hasMore,
		loadMore,
		isSettled: isQuerySettled(result),
	}
}

export type PagedContact = ReturnType<
	typeof usePagedContacts
>["contacts"][number]
